import { useEffect, useState } from 'react';
import './Loading.css';

/**
 * The other half of ConnectionLost: what fills a surface whose contents have
 * not arrived yet.
 *
 * ConnectionLost covers the request that ended badly, and once it existed the
 * silence moved rather than went away — every one of those screens is still
 * blank for the twenty seconds before the deadline decides nothing is coming.
 * On a phone with two bars that is the whole experience: a header, a composer
 * and an empty rectangle between them, which reads as a conversation nobody
 * ever wrote in rather than as one that is on its way.
 *
 * Nothing is drawn for the first moment, and that is the point of the delay
 * rather than a nicety: on a normal connection the page is here inside a couple
 * of hundred milliseconds, and a spinner that appears and vanishes in that time
 * is a flicker that makes a fast app look unsure of itself. Past the delay the
 * wait is long enough to be worth admitting to.
 *
 * Like ConnectionLost it fills whatever it is put in rather than positioning
 * itself, so the same component is one pane beside the chat list, the thread
 * between a header and a composer, and the entire app behind a login.
 */
export function Loading({
  text,
  delayMs = 400,
}: {
  /** Said under the spinner. Left out where the surrounding screen says it. */
  text?: string;
  /** How long the wait has to last before it is admitted to. */
  delayMs?: number;
}) {
  const [due, setDue] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) return;
    const timer = setTimeout(() => setDue(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  // The box is there from the first frame either way: it is what holds the
  // surface's size, and appearing into an empty pane must not move anything.
  return (
    <div className="loading-pane" role="status">
      {due && (
        <>
          <span className="loading-spinner" />
          {text && <div className="loading-text">{text}</div>}
        </>
      )}
    </div>
  );
}
